/**
 * MMOStation - MMO mode space station entity
 * Displays deployed stations on the hex grid with type-specific visuals
 */

import Phaser from 'phaser';
import { axialToPixel, type HexCoord, STATION_FOOTPRINT, getFootprintHexes, ISO_Y_SCALE } from '../../shared/HexUtils';
import type { FootprintSize } from '../../shared/HexUtils';

const HEX_CELL_RADIUS = 42;

const PLAYER_COLORS = [
  0x3388ee, 0xee4444, 0x44cc55, 0xeeee44,
  0xf08822, 0xbb55ee, 0x00ffff, 0xff00ff,
];

const STATION_LABELS: Record<string, string> = {
  waypoint: 'Waypoint',
  depot: 'Depot',
  fortress: 'Fortress',
  capital: 'Capital',
};

export interface MMOStationData {
  id: string;
  userId: string;
  stationType: string;
  position: HexCoord;
  currentHp: number;
  maxHp: number;
  storedAsteroids: number;
  maxStorage: number;
  stationFeePct: number;
  status: string;
  relocationTarget?: { q: number; r: number };
  relocationPathLength?: number;
  relocationTicksPerHex?: number;
  relocationTickCounter?: number;
}

export class MMOStation extends Phaser.GameObjects.Container {
  public id: string;
  public stationType: string;
  public userId: string;
  public hexCoord: HexCoord;
  public currentHp: number;
  public maxHp: number;
  public storedAsteroids: number;
  public maxStorage: number;
  public stationFeePct: number;
  public status: string;

  private stationGraphics: Phaser.GameObjects.Graphics;
  private footprintGraphics: Phaser.GameObjects.Graphics;
  private healthBar: Phaser.GameObjects.Graphics;
  private selectionIndicator: Phaser.GameObjects.Graphics;
  private nameLabel: Phaser.GameObjects.Text;
  private engagementRing: Phaser.GameObjects.Graphics | null = null;
  private engagementTween: Phaser.Tweens.Tween | null = null;
  private _isEngaged: boolean = false;
  private stationSprite: Phaser.GameObjects.Image | null = null;
  private glowGraphics: Phaser.GameObjects.Graphics | null = null;
  private glowTween: Phaser.Tweens.Tween | null = null;
  private moveTween: Phaser.Tweens.Tween | null = null;
  private relocCountdown: Phaser.GameObjects.Text | null = null;
  private relocCountdownTimer: Phaser.Time.TimerEvent | null = null;
  private relocRemainingSeconds: number = 0;

  constructor(scene: Phaser.Scene, data: MMOStationData) {
    const pixel = axialToPixel(data.position.q, data.position.r);
    super(scene, pixel.x, pixel.y);

    this.id = data.id;
    this.stationType = data.stationType;
    this.userId = data.userId;
    this.hexCoord = data.position;
    this.currentHp = data.currentHp;
    this.maxHp = data.maxHp;
    this.storedAsteroids = data.storedAsteroids;
    this.maxStorage = data.maxStorage;
    this.stationFeePct = data.stationFeePct;
    this.status = data.status;

    this.footprintGraphics = this.scene.add.graphics();
    this.add(this.footprintGraphics);

    this.stationGraphics = this.scene.add.graphics();
    this.add(this.stationGraphics);

    this.healthBar = this.scene.add.graphics();
    this.healthBar.setVisible(false);
    this.add(this.healthBar);

    this.selectionIndicator = this.scene.add.graphics();
    this.selectionIndicator.setVisible(false);
    this.add(this.selectionIndicator);

    const footprint = (STATION_FOOTPRINT[this.stationType] || 1) as FootprintSize;
    const bounds = this.getFootprintBounds(footprint);

    this.nameLabel = this.scene.add.text(bounds.cx, bounds.top - 10, '', {
      fontSize: '10px',
      color: '#88ddff',
      fontFamily: '"Share Tech Mono", monospace',
    });
    this.nameLabel.setOrigin(0.5);
    this.nameLabel.setVisible(false);
    this.add(this.nameLabel);

    this.drawVisuals();
    this.setupGlowPulse();

    this.setDepth(7);

    // Extra 10px pad on touch to comfortably meet 48px minimum tap target
    const _isCoarseStation = typeof window !== 'undefined' && window.matchMedia
      && window.matchMedia('(pointer: coarse)').matches;
    const _stationPad = _isCoarseStation ? 20 : 10;
    const hitW = bounds.right - bounds.left + _stationPad * 2;
    const hitH = bounds.bottom - bounds.top + _stationPad * 2;
    this.setSize(hitW, hitH);
    this.setInteractive(
      new Phaser.Geom.Rectangle(bounds.left - _stationPad, bounds.top - _stationPad, hitW, hitH),
      Phaser.Geom.Rectangle.Contains,
    );

    // NOTE: No sprite-level 'pointerdown' handler — see MMOUnit.ts for rationale.
    // Selection is handled in MMOGameScene.onPointerUp via getEntityAtWorldPoint().
  }

  public updateFromServer(data: MMOStationData): void {
    if (!this.scene || !this.scene.sys) return;

    this.currentHp = data.currentHp;
    this.maxHp = data.maxHp;
    this.storedAsteroids = data.storedAsteroids;
    this.maxStorage = data.maxStorage;
    this.stationFeePct = data.stationFeePct;
    this.status = data.status;

    if (data.position.q !== this.hexCoord.q || data.position.r !== this.hexCoord.r) {
      this.hexCoord = data.position;
      const pixel = axialToPixel(this.hexCoord.q, this.hexCoord.r);

      if (this.moveTween) {
        this.moveTween.destroy();
        this.moveTween = null;
      }

      // Use 850ms tween per hop during relocation (like unit movement), 4s otherwise
      const duration = data.status === 'relocating' ? 850 : 4000;
      this.moveTween = this.scene.tweens.add({
        targets: this,
        x: pixel.x,
        y: pixel.y,
        duration,
        ease: 'Sine.easeInOut',
      });
    }

    // Update relocation countdown
    this.updateRelocationCountdown(data);

    this.drawVisuals();
  }

  private updateRelocationCountdown(data: MMOStationData): void {
    if (data.status === 'relocating' && data.relocationPathLength != null && data.relocationTicksPerHex != null) {
      const tickCounter = data.relocationTickCounter ?? 0;
      this.relocRemainingSeconds = data.relocationPathLength * data.relocationTicksPerHex - tickCounter;

      if (!this.relocCountdown) {
        const footprint = (STATION_FOOTPRINT[this.stationType] || 1) as FootprintSize;
        const bounds = this.getFootprintBounds(footprint);
        this.relocCountdown = this.scene.add.text(bounds.cx, bounds.top - 24, '', {
          fontSize: '11px',
          color: '#ffcc00',
          fontFamily: '"Share Tech Mono", monospace',
          backgroundColor: '#00000088',
          padding: { left: 3, right: 3, top: 1, bottom: 1 },
        });
        this.relocCountdown.setOrigin(0.5);
        this.add(this.relocCountdown);
      }
      this.relocCountdown.setText(`${Math.max(0, this.relocRemainingSeconds)}s`);
      this.relocCountdown.setVisible(true);

      // Start or restart a local 1-second timer to count down between server updates
      if (this.relocCountdownTimer) this.relocCountdownTimer.destroy();
      this.relocCountdownTimer = this.scene.time.addEvent({
        delay: 1000,
        loop: true,
        callback: () => {
          this.relocRemainingSeconds = Math.max(0, this.relocRemainingSeconds - 1);
          if (this.relocCountdown) {
            this.relocCountdown.setText(`${this.relocRemainingSeconds}s`);
          }
        },
      });
    } else {
      if (this.relocCountdown) {
        this.relocCountdown.setVisible(false);
      }
      if (this.relocCountdownTimer) {
        this.relocCountdownTimer.destroy();
        this.relocCountdownTimer = null;
      }
    }
  }

  public setVisibility(fullyVisible: boolean, explored: boolean): void {
    if (!explored) {
      this.setAlpha(0);
    } else if (!fullyVisible) {
      this.setAlpha(0.35);
    } else {
      this.setAlpha(1);
    }
  }

  public setSelected(selected: boolean): void {
    this.selectionIndicator.setVisible(selected);
    this.healthBar.setVisible(selected || this._isEngaged);
  }

  private drawVisuals(): void {
    this.drawFootprintHighlight();
    this.drawStation();
    this.drawHealthBar();
    this.drawSelectionRing();
    this.updateLabels();
  }

  private getOwnerColor(): number {
    let hash = 0;
    for (let i = 0; i < this.userId.length; i++) {
      hash = (hash << 5) - hash + this.userId.charCodeAt(i);
      hash = hash & hash;
    }
    return PLAYER_COLORS[Math.abs(hash) % PLAYER_COLORS.length];
  }

  private drawFootprintHighlight(): void {
    this.footprintGraphics.clear();
    const color = this.getOwnerColor();
    const footprint = (STATION_FOOTPRINT[this.stationType] || 1) as FootprintSize;
    const hexes = getFootprintHexes(this.hexCoord, footprint);
    const anchorPixel = axialToPixel(this.hexCoord.q, this.hexCoord.r);

    for (const hex of hexes) {
      const p = axialToPixel(hex.q, hex.r);
      const ox = p.x - anchorPixel.x;
      const oy = p.y - anchorPixel.y;

      this.footprintGraphics.fillStyle(color, 0.12);
      this.drawHexagon(this.footprintGraphics, ox, oy, HEX_CELL_RADIUS, true);
      this.footprintGraphics.lineStyle(1.5, color, 0.35);
      this.drawHexagon(this.footprintGraphics, ox, oy, HEX_CELL_RADIUS, false);
    }
  }

  private getStationSpriteKey(): string | null {
    const key = `station-${this.stationType}`;
    return this.scene.textures.exists(key) ? key : null;
  }

  private getSpriteOffset(): { x: number; y: number } {
    const footprint = (STATION_FOOTPRINT[this.stationType] || 1) as FootprintSize;
    if (footprint === 2) {
      const hexes = getFootprintHexes(this.hexCoord, footprint);
      const anchorPixel = axialToPixel(this.hexCoord.q, this.hexCoord.r);
      const secondPixel = axialToPixel(hexes[1].q, hexes[1].r);
      return {
        x: (secondPixel.x - anchorPixel.x) / 2,
        y: (secondPixel.y - anchorPixel.y) / 2 - 10,
      };
    }
    return { x: 0, y: -14 };
  }

  private drawStation(): void {
    this.stationGraphics.clear();

    const footprint = (STATION_FOOTPRINT[this.stationType] || 1) as FootprintSize;
    const spriteKey = this.getStationSpriteKey();

    if (spriteKey) {
      this.stationGraphics.setVisible(false);
      let spriteSize: number;
      if (footprint === 7) spriteSize = 220;
      else if (footprint >= 2) spriteSize = 140;
      else spriteSize = 80;

      const offset = this.getSpriteOffset();

      if (!this.stationSprite) {
        this.stationSprite = this.scene.add.image(offset.x, offset.y, spriteKey);
        this.add(this.stationSprite);
      } else if (this.stationSprite.texture.key !== spriteKey) {
        this.stationSprite.setTexture(spriteKey);
      }
      this.stationSprite.setPosition(offset.x, offset.y);
      this.stationSprite.setDisplaySize(spriteSize, spriteSize);
      this.stationSprite.setVisible(true);
    } else {
      if (this.stationSprite) this.stationSprite.setVisible(false);
      this.stationGraphics.setVisible(true);

      const color = this.getOwnerColor();

      if (footprint > 1) {
        const iconSize = footprint === 7 ? 24 : 16;
        this.stationGraphics.fillStyle(color, 0.9);
        if (this.stationType === 'fortress') {
          const cw = iconSize * 0.25;
          const ch = iconSize;
          this.stationGraphics.fillRect(-cw, -ch, cw * 2, ch * 2);
          this.stationGraphics.fillRect(-ch, -cw, ch * 2, cw * 2);
        } else if (this.stationType === 'capital') {
          this.stationGraphics.fillCircle(0, 0, iconSize);
          this.stationGraphics.fillStyle(0x000000, 0.5);
          this.stationGraphics.fillCircle(0, 0, iconSize * 0.5);
        }
      } else {
        const size = this.getStationSize();
        this.stationGraphics.lineStyle(2, color, 0.9);
        this.drawHexagon(this.stationGraphics, 0, 0, size + 2, false);
        this.stationGraphics.fillStyle(color, 0.4);
        this.drawHexagon(this.stationGraphics, 0, 0, size, true);
        this.stationGraphics.fillStyle(color, 0.8);
        if (this.stationType === 'waypoint') {
          this.stationGraphics.fillCircle(0, 0, 4);
        } else if (this.stationType === 'depot') {
          this.stationGraphics.fillRect(-5, -5, 10, 10);
        }
      }
    }
  }

  private drawHealthBar(): void {
    this.healthBar.clear();
    const footprint = (STATION_FOOTPRINT[this.stationType] || 1) as FootprintSize;
    const bounds = this.getFootprintBounds(footprint);
    const barWidth = Math.max(40, bounds.right - bounds.left);
    const barHeight = 4;
    const barY = bounds.bottom + 2;

    this.healthBar.fillStyle(0x330000, 0.7);
    this.healthBar.fillRect(bounds.cx - barWidth / 2, barY, barWidth, barHeight);

    const hpPct = this.currentHp / this.maxHp;
    const hpColor = hpPct > 0.5 ? 0x00ff44 : hpPct > 0.25 ? 0xffff00 : 0xff3300;
    this.healthBar.fillStyle(hpColor, 0.9);
    this.healthBar.fillRect(bounds.cx - barWidth / 2, barY, barWidth * hpPct, barHeight);
  }

  private drawSelectionRing(): void {
    this.selectionIndicator.clear();
    const color = this.getOwnerColor();
    const footprint = (STATION_FOOTPRINT[this.stationType] || 1) as FootprintSize;
    const hexes = getFootprintHexes(this.hexCoord, footprint);
    const anchorPixel = axialToPixel(this.hexCoord.q, this.hexCoord.r);

    this.selectionIndicator.lineStyle(2, color, 0.8);
    for (const hex of hexes) {
      const p = axialToPixel(hex.q, hex.r);
      this.drawHexagon(this.selectionIndicator, p.x - anchorPixel.x, p.y - anchorPixel.y, HEX_CELL_RADIUS + 2, false);
    }
  }

  private updateLabels(): void {
    const label = STATION_LABELS[this.stationType] || this.stationType;
    this.nameLabel.setText(label);

    const color = this.getOwnerColor();
    this.nameLabel.setColor(`#${color.toString(16).padStart(6, '0')}`);
  }

  private getStationSize(): number {
    switch (this.stationType) {
      case 'depot': return 18;
      default: return 15;
    }
  }

  private getFootprintBounds(footprint: FootprintSize): { left: number; right: number; top: number; bottom: number; cx: number; cy: number } {
    if (footprint <= 1) {
      const s = 44;
      return { left: -s, right: s, top: -s, bottom: s, cx: 0, cy: 0 };
    }

    const hexes = getFootprintHexes(this.hexCoord, footprint);
    const anchorPixel = axialToPixel(this.hexCoord.q, this.hexCoord.r);
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;

    for (const hex of hexes) {
      const p = axialToPixel(hex.q, hex.r);
      const ox = p.x - anchorPixel.x;
      const oy = p.y - anchorPixel.y;
      minX = Math.min(minX, ox - HEX_CELL_RADIUS);
      maxX = Math.max(maxX, ox + HEX_CELL_RADIUS);
      minY = Math.min(minY, oy - HEX_CELL_RADIUS);
      maxY = Math.max(maxY, oy + HEX_CELL_RADIUS);
    }

    return { left: minX, right: maxX, top: minY, bottom: maxY, cx: (minX + maxX) / 2, cy: (minY + maxY) / 2 };
  }

  public setEngaged(engaged: boolean): void {
    if (this._isEngaged === engaged) return;
    this._isEngaged = engaged;

    const footprint = (STATION_FOOTPRINT[this.stationType] || 1) as FootprintSize;
    const bounds = this.getFootprintBounds(footprint);
    const ringRadius = Math.max(bounds.right - bounds.left, bounds.bottom - bounds.top) / 2 + 6;

    this.healthBar.setVisible(engaged || this.selectionIndicator.visible);

    if (engaged) {
      if (!this.engagementRing) {
        this.engagementRing = this.scene.add.graphics();
        this.add(this.engagementRing);
        this.sendToBack(this.engagementRing);
      }
      this.engagementRing.clear();
      this.engagementRing.lineStyle(2, 0xff3333, 0.8);
      this.engagementRing.strokeCircle(bounds.cx, bounds.cy, ringRadius);
      this.engagementRing.setAlpha(0.8);

      if (this.engagementTween) {
        this.engagementTween.destroy();
      }
      this.engagementTween = this.scene.tweens.add({
        targets: this.engagementRing,
        alpha: { from: 0.3, to: 0.8 },
        duration: 600,
        yoyo: true,
        repeat: -1,
      });
    } else {
      if (this.engagementTween) {
        this.engagementTween.destroy();
        this.engagementTween = null;
      }
      if (this.engagementRing) {
        this.engagementRing.clear();
        this.engagementRing.setAlpha(0);
      }
    }
  }

  private setupGlowPulse(): void {
    const color = this.getOwnerColor();
    const footprint = (STATION_FOOTPRINT[this.stationType] || 1) as FootprintSize;

    this.glowGraphics = this.scene.add.graphics();
    this.add(this.glowGraphics);
    this.sendToBack(this.glowGraphics);

    const hexes = getFootprintHexes(this.hexCoord, footprint);
    const anchorPixel = axialToPixel(this.hexCoord.q, this.hexCoord.r);
    this.glowGraphics.lineStyle(3, color, 0.3);
    for (const hex of hexes) {
      const p = axialToPixel(hex.q, hex.r);
      this.drawHexagon(this.glowGraphics, p.x - anchorPixel.x, p.y - anchorPixel.y, HEX_CELL_RADIUS + 4, false);
    }
    this.glowGraphics.setAlpha(0.3);

    this.glowTween = this.scene.tweens.add({
      targets: this.glowGraphics,
      alpha: { from: 0.2, to: 0.5 },
      duration: 2000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  public destroy(fromScene?: boolean): void {
    if (this.moveTween) this.moveTween.destroy();
    if (this.engagementTween) this.engagementTween.destroy();
    if (this.engagementRing) this.engagementRing.destroy();
    if (this.glowTween) this.glowTween.destroy();
    if (this.glowGraphics) this.glowGraphics.destroy();
    if (this.relocCountdown) this.relocCountdown.destroy();
    if (this.relocCountdownTimer) this.relocCountdownTimer.destroy();
    super.destroy(fromScene);
  }

  private drawHexagon(
    graphics: Phaser.GameObjects.Graphics,
    cx: number, cy: number, size: number, fill: boolean,
  ): void {
    graphics.beginPath();
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 3) * i;
      const px = cx + size * Math.cos(angle);
      const py = cy + size * Math.sin(angle) * ISO_Y_SCALE;
      if (i === 0) graphics.moveTo(px, py);
      else graphics.lineTo(px, py);
    }
    graphics.closePath();
    if (fill) graphics.fillPath();
    else graphics.strokePath();
  }
}
